import React, { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'tr' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations: Record<string, Record<Language, string>> = {
  // General
  'logout': { tr: 'Çıkış Yap', en: 'Logout' },
  'back': { tr: 'Geri', en: 'Back' },
  'save': { tr: 'Kaydet', en: 'Save' },
  'cancel': { tr: 'İptal', en: 'Cancel' },
  'confirm': { tr: 'Onayla', en: 'Confirm' },
  'edit': { tr: 'Düzenle', en: 'Edit' },
  'delete': { tr: 'Sil', en: 'Delete' },
  'error': { tr: 'Bir hata oluştu', en: 'An error occurred' },

  // Header
  'member_login': { tr: 'Üye Girişi', en: 'Login' },
  'corp_register': { tr: 'Kurumsal Üye Ol', en: 'Corporate Register' },
  'logged_in': { tr: 'Giriş Yapıldı', en: 'Logged In' },
  'my_account': { tr: 'Hesabım', en: 'My Account' },
  'admin_panel': { tr: 'Yönetici Paneli', en: 'Admin Panel' },
  'my_requests': { tr: 'Taleplerim', en: 'My Requests' },

  // Hero
  'hero_badge': { tr: 'Yeni Nesil Pazar Yeri', en: 'Next Gen Marketplace' },
  'hero_title_prefix': { tr: 'Sen İste,', en: 'You Ask,' },
  'hero_title_brand': { tr: 'Teklif ', en: 'Offers ' },
  'hero_title_suffix': { tr: 'Cebine Gelsin', en: 'Come to You' },
  'hero_slogan': { tr: 'Ev ve Araba bulmanın en kolay yolu', en: 'The easiest way to find Home and Car' },
  'hero_desc': { tr: 'Emlak ve araç alım satımında aramakla vakit kaybetme. Kriterlerini gir, seçkin firmalardan en iyi teklifler sana gelsin.', en: 'Don\'t waste time searching for real estate and cars. Enter your criteria, get the best offers from elite companies.' },
  'card_real_estate': { tr: 'Emlak', en: 'Real Estate' },
  'card_real_estate_desc': { tr: 'Satılık veya Kiralık Konut, İşyeri, Arsa', en: 'For Sale or Rent: Housing, Workplace, Land' },
  'card_car': { tr: 'Vasıta', en: 'Vehicles' },
  'card_car_desc': { tr: 'İkinci El ve Sıfır Araçlar', en: 'Used and New Vehicles' },
  'btn_create_request': { tr: 'Hemen Talep Oluştur', en: 'Create Request Now' },

  // Stats
  'stat_happy_customers': { tr: 'Mutlu Müşteri', en: 'Happy Customers' },
  'stat_approved_providers': { tr: 'Onaylı Firma', en: 'Approved Firms' },
  'stat_total_leads': { tr: 'Toplam Talep', en: 'Total Leads' },
  'stat_score': { tr: 'Memnuniyet', en: 'Satisfaction' },

  // How it works
  'how_badge': { tr: 'Sistem Nasıl İşler?', en: 'How it Works?' },
  'how_title': { tr: '3 Adımda Kolayca Sonuca Ulaş', en: 'Reach Results Easily in 3 Steps' },
  'how_desc': { tr: 'Karmaşık süreçlerle uğraşma. İhtiyacını belirt, arkana yaslan.', en: 'Don\'t deal with complex processes. State your need, sit back.' },
  'step_1_title': { tr: 'Talebini Oluştur', en: 'Create Request' },
  'step_1_desc': { tr: 'Aradığın emlak veya aracın özelliklerini detaylıca gir.', en: 'Enter the details of the property or vehicle you are looking for.' },
  'step_2_title': { tr: 'Teklifleri Topla', en: 'Collect Offers' },
  'step_2_desc': { tr: 'Onaylı kurumsal firmalar sana özel tekliflerini sunsun.', en: 'Let approved corporate firms present their offers to you.' },
  'step_3_title': { tr: 'Seç ve Kazan', en: 'Choose and Win' },
  'step_3_desc': { tr: 'En uygun teklifi seç, iletişime geç ve anlaş.', en: 'Choose the best offer, contact and agree.' },

  // Footer
  'footer_intro': { tr: 'Ev ve Araba bulmanın en kolay yolu', en: 'The easiest way to find Home and Car' },
  'footer_cat': { tr: 'Kategoriler', en: 'Categories' },
  'footer_cat_1': { tr: 'Satılık Konut', en: 'Housing for Sale' },
  'footer_cat_2': { tr: 'Kiralık Konut', en: 'Housing for Rent' },
  'footer_cat_3': { tr: 'Arsa & Arazi', en: 'Land & Terrain' },
  'footer_cat_4': { tr: 'Otomobil', en: 'Automobile' },
  'footer_corp': { tr: 'Kurumsal', en: 'Corporate' },
  'footer_corp_1': { tr: 'Hakkımızda', en: 'About Us' },
  'footer_corp_2': { tr: 'Nasıl Çalışır?', en: 'How it Works?' },
  'footer_corp_4': { tr: 'İletişim', en: 'Contact' },
  'footer_follow': { tr: 'Bizi Takip Edin', en: 'Follow Us' },
  'footer_rights': { tr: 'Tüm hakları saklıdır.', en: 'All rights reserved.' },

  // Request Form
  'req_title_estate': { tr: 'Gayrimenkul Arıyorum', en: 'Looking for Real Estate' },
  'req_title_car': { tr: 'Araç Arıyorum', en: 'Looking for Vehicle' },
  'req_subtitle': { tr: 'Hayalindeki seçeneği bulmak için detayları bizimle paylaş.', en: 'Share details to find your dream option.' },
  'req_type_sale': { tr: 'Satılık', en: 'For Sale' },
  'req_type_rent': { tr: 'Kiralık', en: 'For Rent' },
  'req_cat_housing': { tr: 'Konut', en: 'Housing' },
  'req_cat_workplace': { tr: 'İş Yeri', en: 'Workplace' },
  'req_cat_land': { tr: 'Arsa', en: 'Land' },
  'req_city': { tr: 'İl', en: 'City' },
  'req_district': { tr: 'İlçe', en: 'District' },
  'req_select': { tr: 'Seçiniz', en: 'Select' },
  'req_min': { tr: 'Min', en: 'Min' },
  'req_max': { tr: 'Maks', en: 'Max' },
  'req_budget_header': { tr: 'Bütçe Aralığı', en: 'Budget Range' },
  'req_currency': { tr: 'Para Birimi', en: 'Currency' },
  'req_title_label': { tr: 'Talep Başlığı', en: 'Request Title' },
  'req_desc_label': { tr: 'Açıklama & Detaylar', en: 'Description & Details' },
  'req_desc_ph': { tr: 'Örn: Güney cephe olsun, metroya yakın olsun...', en: 'Ex: South facing, close to metro...' },
  'req_desc_ph_estate': { tr: 'Örn: Güney cephe olsun, metroya yakın olsun, en az 2 banyosu olsun, krediye uygun olsun...', en: 'Ex: South facing, near metro, at least 2 baths...' },
  'req_desc_ph_car': { tr: 'Örn: Hatasız, boyasız, tramer kaydı olmayan, sigara içilmemiş, servis bakımlı...', en: 'Ex: Accident-free, paint-free, no tramer record...' },
  'req_offers_label': { tr: 'Kaç Teklif İstiyorsun?', en: 'How Many Offers?' },
  'req_offer_unlimited': { tr: 'Limitsiz', en: 'Unlimited' },
  'req_contact_header': { tr: 'İletişim Bilgileri', en: 'Contact Info' },
  'req_contact_note': { tr: 'Bu bilgiler sadece teklifini kabul ettiğiniz firmalarla paylaşılacaktır.', en: 'This info will only be shared with firms whose offers you accept.' },
  'req_btn_next': { tr: 'Devam Et', en: 'Continue' },
  'req_btn_verify_publish': { tr: 'Doğrula ve Yayınla', en: 'Verify and Publish' },
  'req_security_title': { tr: 'Güvenlik Doğrulaması', en: 'Security Verification' },
  'req_security_desc': { tr: 'Telefonunuza gönderilen 4 haneli kodu giriniz.', en: 'Enter the 4-digit code sent to your phone.' },
  'req_success_title': { tr: 'Talebiniz Alındı!', en: 'Request Received!' },
  'req_success_desc': { tr: 'Talebiniz başarıyla oluşturuldu ve ilgili kurumsal firmalara iletildi. Teklifler gelmeye başladığında sizi bilgilendireceğiz.', en: 'Your request has been successfully created and forwarded to relevant corporate firms. We will notify you when offers start arriving.' },
  'req_btn_home': { tr: 'Anasayfaya Dön', en: 'Return to Home' },
  'req_ph_home': { tr: 'Örn: Kadıköy Moda\'da 2+1 Kiralık Daire', en: 'Ex: 2+1 Flat for Rent in Kadikoy Moda' },
  'req_ph_office': { tr: 'Örn: Maslak\'ta Plaza Katı', en: 'Ex: Plaza Floor in Maslak' },
  'req_ph_land': { tr: 'Örn: Silivri\'de İmarlı Arsa', en: 'Ex: Zoned Land in Silivri' },
  'req_ph_car': { tr: 'Örn: 2020 Model Passat, Düşük KM', en: 'Ex: 2020 Model Passat, Low Mileage' },
  'req_urgent_title': { tr: 'Aciliyet Durumu', en: 'Urgency Status' },
  'req_buyer_status_label': { tr: 'Satın Alma Durumu', en: 'Buying Status' },
  'req_buyer_standard': { tr: 'Standart Alıcı', en: 'Standard Buyer' },
  'req_buyer_standard_desc': { tr: 'Piyasayı araştırıyorum, uygun olursa alacağım.', en: 'I am researching the market, I will buy if suitable.' },
  'req_buyer_serious': { tr: 'Ciddi Alıcı', en: 'Serious Buyer' },
  'req_buyer_serious_desc': { tr: 'Nakit/Kredim hazır, hemen alım yapabilirim.', en: 'Cash/Credit ready, I can buy immediately.' },
  'req_legal_kvkk': { tr: 'KVKK Aydınlatma Metni\'ni okudum ve onaylıyorum.', en: 'I read and approve the KVKK Text.' },
  'req_legal_share': { tr: 'Bilgilerimin hizmet sağlayıcılarla paylaşılmasına izin veriyorum.', en: 'I allow my information to be shared with service providers.' },

  // Labels
  'req_label_room': { tr: 'Oda Sayısı', en: 'Rooms' },
  'req_label_age': { tr: 'Bina Yaşı', en: 'Building Age' },
  'req_label_floor': { tr: 'Bulunduğu Kat', en: 'Floor' },
  'req_label_heating': { tr: 'Isıtma', en: 'Heating' },
  'req_label_bath': { tr: 'Banyo Sayısı', en: 'Bathrooms' },
  'req_label_kitchen': { tr: 'Mutfak Tipi', en: 'Kitchen Type' },
  'req_label_balcony': { tr: 'Balkon', en: 'Balcony' },
  'req_label_elevator': { tr: 'Asansör', en: 'Elevator' },
  'req_label_parking': { tr: 'Otopark', en: 'Parking' },
  'req_label_furniture': { tr: 'Eşya Durumu', en: 'Furniture' },
  'req_label_site': { tr: 'Site İçerisinde', en: 'In Complex' },
  'req_label_credit': { tr: 'Krediye Uygun', en: 'Credit Suitable' },
  'req_label_deed': { tr: 'Tapu Durumu', en: 'Deed Status' },
  'req_label_usage': { tr: 'Kullanım Durumu', en: 'Usage Status' },
  'req_label_extra': { tr: 'Ekstra Özellikler', en: 'Extra Features' },
  'req_label_sale_details': { tr: 'Satış Detayları', en: 'Sale Details' },
  'req_label_brand': { tr: 'Marka', en: 'Brand' },
  'req_label_model': { tr: 'Model', en: 'Model' },
  'req_label_year_range': { tr: 'Yıl Aralığı', en: 'Year Range' },
  'req_label_km_range': { tr: 'KM Aralığı', en: 'KM Range' },
  'req_label_fuel': { tr: 'Yakıt', en: 'Fuel' },
  'req_label_gear': { tr: 'Vites', en: 'Gear' },
  'req_label_body': { tr: 'Kasa Tipi', en: 'Body Type' },
  'req_label_color': { tr: 'Renk', en: 'Color' },
  'req_label_damage': { tr: 'Hasar Durumu', en: 'Damage Status' },
  'req_ph_brand': { tr: 'Önce Marka Seçiniz', en: 'Select Brand First' },

  // Doping
  'doping_std_label': { tr: 'Standart', en: 'Standard' },
  'doping_std_price': { tr: 'Ücretsiz', en: 'Free' },
  'doping_std_desc': { tr: 'Talebiniz standart sırada yayınlanır.', en: 'Your request is published in standard order.' },
  'doping_urgent_label': { tr: 'Hızlı Satış Dopingi', en: 'Fast Sell Doping' },
  'doping_urgent_price': { tr: '249 TL', en: '249 TL' },
  'doping_urgent_desc': { tr: 'Talebiniz 3 gün boyunca "Acil" etiketiyle öne çıkar.', en: 'Your request stands out with "Urgent" tag for 3 days.' },
  'doping_showcase_label': { tr: 'Vitrini Yakala', en: 'Showcase' },
  'doping_showcase_price': { tr: '250 TL', en: '250 TL' },
  'doping_showcase_desc': { tr: 'Anasayfa vitrininde 48 saat listelenir.', en: 'Listed on homepage showcase for 48 hours.' },
  'doping_note_prefix': { tr: 'Hızlı Satış Dopingi alan ilanlar, standart ilanlara göre', en: 'Doping listings are viewed' },
  'doping_note_suffix': { tr: 'daha hızlı dönüş alır.', en: 'times faster than standard listings.' },
  'doping_days_left': { tr: 'Gün Kaldı', en: 'Days Left' },
  'doping_hours_left': { tr: 'Saat Kaldı', en: 'Hours Left' },
  'doping_expired': { tr: 'Süresi Doldu', en: 'Expired' },
  'doping_modal_title': { tr: 'Hızlı Satış Dopingi', en: 'Fast Sell Doping' },
  'doping_modal_desc': { tr: 'İlanınızı öne çıkararak daha hızlı teklif alın.', en: 'Get offers faster by highlighting your listing.' },
  'doping_btn_buy': { tr: 'Hemen Satın Al', en: 'Buy Now' },
  'doping_btn_extend': { tr: 'Süreyi Uzat', en: 'Extend' },
  'doping_payment_note': { tr: 'Satın alınan hizmet kapsamındaki haklar iade edilemez ve devredilemez. Tüm ödeme işlemleri PayTR altyapısı ve 256-bit SSL güvencesiyle gerçekleştirilmektedir.', en: 'The rights included in the purchased service are non-refundable and non-transferable. All payment transactions are processed using PayTR infrastructure and secured with 256-bit SSL.' },

  // Auth
  'auth_tab_customer': { tr: 'Müşteri Girişi', en: 'Customer Login' },
  'auth_tab_provider_login': { tr: 'Kurumsal Giriş', en: 'Corporate Login' },
  'auth_tab_broker_login': { tr: 'Broker Girişi', en: 'Broker Login' },
  'auth_tab_provider_register': { tr: 'Kurumsal Kayıt', en: 'Corporate Register' },
  'auth_customer_title': { tr: 'Hızlı Giriş Yap', en: 'Quick Login' },
  'auth_customer_desc': { tr: 'Telefon numaranızla saniyeler içinde giriş yapın.', en: 'Login in seconds with your phone number.' },
  'auth_label_phone': { tr: 'Cep Telefonu', en: 'Mobile Phone' },
  'auth_label_email': { tr: 'E-Posta Adresi', en: 'Email Address' },
  'auth_label_pass': { tr: 'Şifre', en: 'Password' },
  'auth_placeholder_phone': { tr: '5XX XXX XX XX', en: '5XX XXX XX XX' },
  'auth_btn_login': { tr: 'Giriş Yap', en: 'Login' },
  'auth_btn_register': { tr: 'Kayıt Ol', en: 'Register' },
  'auth_processing': { tr: 'İşleniyor...', en: 'Processing...' },
  'auth_sending_sms': { tr: 'SMS Gönderiliyor...', en: 'Sending SMS...' },
  'auth_logging_in': { tr: 'Giriş Yapılıyor...', en: 'Logging in...' },
  'auth_verify_title': { tr: 'Doğrulama Kodu', en: 'Verification Code' },
  'auth_verify_desc': { tr: 'Telefonunuza gönderilen 4 haneli kodu giriniz.', en: 'Enter the 4-digit code sent to your phone.' },
  'auth_btn_verify': { tr: 'Doğrula', en: 'Verify' },
  'auth_kvkk': { tr: 'Devam ederek Kullanıcı Sözleşmesi\'ni kabul etmiş olursunuz.', en: 'By continuing, you agree to the User Agreement.' },
  'auth_success_title': { tr: 'İşlem Başarılı', en: 'Success' },
  'auth_success_desc': { tr: 'Giriş işleminiz başarıyla tamamlandı.', en: 'Login completed successfully.' },
  'auth_type_estate': { tr: 'Emlak Ofisi', en: 'Real Estate Office' },
  'auth_type_car': { tr: 'Oto Galeri', en: 'Auto Gallery' },
  'auth_ph_company': { tr: 'Firma Unvanı', en: 'Company Name' },
  'auth_ph_vkn': { tr: 'Vergi No (VKN)', en: 'Tax ID' },
  'auth_ph_contact': { tr: 'Ad Soyad', en: 'Full Name' },
  'auth_ph_pass': { tr: 'Şifre Belirle', en: 'Set Password' },
  'auth_upload_doc': { tr: 'Yetki Belgesi Yükle', en: 'Upload Auth Doc' },
  'auth_upload_logo': { tr: 'Logo Yükle', en: 'Upload Logo' },
  'auth_err_phone': { tr: 'Geçerli bir telefon numarası giriniz.', en: 'Enter a valid phone number.' },
  'auth_err_fields': { tr: 'Lütfen tüm alanları doldurunuz.', en: 'Please fill in all fields.' },
  'auth_err_creds': { tr: 'Hatalı e-posta veya şifre.', en: 'Invalid email or password.' },
  'auth_ph_phone': { tr: '5XX XXX XX XX', en: '5XX XXX XX XX' },
  'auth_ph_email': { tr: 'isim@ornek.com', en: 'name@example.com' },

  // Admin / Provider Dashboard
  'admin_title': { tr: 'Yönetim Paneli', en: 'Admin Panel' },
  'admin_desc': { tr: 'Platform istatistikleri ve yönetimi.', en: 'Platform stats and management.' },
  'admin_tab_members': { tr: 'Üyeler', en: 'Members' },
  'admin_tab_leads': { tr: 'İlanlar', en: 'Listings' },
  'prov_tab_pool': { tr: 'Fırsat Havuzu', en: 'Lead Pool' },
  'prov_tab_offers': { tr: 'Tekliflerim', en: 'My Offers' },
  'prov_tab_my_offers': { tr: 'Tekliflerim', en: 'My Offers' }, // Alias for backward compatibility
  'prov_tab_portfolio': { tr: 'Portföyüm', en: 'My Portfolio' },
  'prov_tab_wallet': { tr: 'Cüzdan', en: 'Wallet' },
  'prov_tab_profile': { tr: 'Profilim', en: 'My Profile' },
  'prov_tab_reviews': { tr: 'Değerlendirme', en: 'Reviews' },
  'prov_tab_alarms': { tr: 'Alarmlar', en: 'Alarms' }, // Added Key
  'prov_no_leads': { tr: 'Şu an uygun fırsat bulunmuyor.', en: 'No suitable leads at the moment.' },
  'prov_filter_label': { tr: 'Filtrele', en: 'Filter' },
  'prov_search_ph': { tr: 'Şehir veya kelime ara...', en: 'Search city or keyword...' },
  'prov_filter_all_types': { tr: 'Tümü', en: 'All' },
  'prov_btn_unlock': { tr: 'Bilgileri Aç', en: 'Unlock Info' },
  'prov_btn_contact_open': { tr: 'Bilgiler Açık', en: 'Info Unlocked' },
  'prov_portfolio_add': { tr: 'İlan Ekle', en: 'Add Listing' },
  'prov_label_price': { tr: 'Fiyat', en: 'Price' },
  'prov_label_msg': { tr: 'Mesajınız', en: 'Your Message' },
  'prov_label_category': { tr: 'Kategori', en: 'Category' },
  'prov_modal_offer_title': { tr: 'Teklif Gönder', en: 'Send Offer' },
  'prov_edit_profile_success': { tr: 'Profil güncellendi.', en: 'Profile updated.' },
  'prov_pass_success': { tr: 'Şifre başarıyla değiştirildi.', en: 'Password changed successfully.' },
  'prov_alarm_btn': { tr: 'Alarmlar', en: 'Alarms' },

  // Customer Dashboard
  'cust_tab_leads': { tr: 'Taleplerim', en: 'My Requests' },
  'cust_tab_settings': { tr: 'Ayarlar', en: 'Settings' },
  'cust_btn_new': { tr: 'Yeni Talep', en: 'New Request' },
  'cust_no_leads_title': { tr: 'Henüz Talep Oluşturmadınız', en: 'No Requests Yet' },
  'cust_no_leads_desc': { tr: 'Hemen yeni bir talep oluşturarak teklifleri toplamaya başlayın.', en: 'Create a new request now and start collecting offers.' },
  'cust_btn_doping': { tr: 'Doping Al', en: 'Get Doping' },
  'cust_offers_received': { tr: 'Gelen Teklifler', en: 'Received Offers' },
  'cust_no_offers': { tr: 'Henüz teklif gelmedi. Biraz bekleyin veya doping alarak öne çıkın.', en: 'No offers yet. Wait a bit or get doping to stand out.' },
  'cust_btn_view_listing': { tr: 'İlanı İncele', en: 'View Listing' },
  'cust_link_catalog': { tr: 'Katalog / Görsel', en: 'Catalog / Image' },
  'cust_btn_rate': { tr: 'Puanla', en: 'Rate' },
  'cust_modal_edit_title': { tr: 'Talebi Düzenle', en: 'Edit Request' },
  'cust_modal_rate_title': { tr: 'Hizmeti Değerlendir', en: 'Rate Service' },
  'cust_modal_rate_desc': { tr: 'Lütfen aldığınız hizmeti puanlayın:', en: 'Please rate the service you received:' },
  'cust_ph_comment': { tr: 'Yorumunuz...', en: 'Your comment...' },
  'cust_btn_save': { tr: 'Değişiklikleri Kaydet', en: 'Save Changes' },
  'cust_profile_name': { tr: 'Ad Soyad', en: 'Full Name' },
  'cust_profile_email': { tr: 'E-Posta', en: 'Email' },
  'cust_profile_phone': { tr: 'Telefon', en: 'Phone' },
  'cust_profile_addr': { tr: 'Adres', en: 'Address' },

  // Payment
  'pay_title': { tr: 'Ödeme Bilgileri', en: 'Payment Details' },
  'pay_card_holder': { tr: 'Kart Sahibi', en: 'Card Holder' },
  'pay_card_number': { tr: 'Kart Numarası', en: 'Card Number' },
  'pay_expiry': { tr: 'Son Kullanma (AA/YY)', en: 'Expiry (MM/YY)' },
  'pay_cvv': { tr: 'CVV', en: 'CVV' },
  'pay_processing': { tr: 'Ödeme İşleniyor...', en: 'Processing Payment...' },
  'pay_success': { tr: 'Ödeme Başarılı!', en: 'Payment Successful!' },

  // Constants / Options
  'opt_room_studio': { tr: 'Stüdyo (1+0)', en: 'Studio' },
  'opt_room_1_1': { tr: '1+1', en: '1+1' },
  'opt_room_2_0': { tr: '2+0', en: '2+0' },
  'opt_room_2_1': { tr: '2+1', en: '2+1' },
  'opt_room_2_2': { tr: '2+2', en: '2+2' },
  'opt_room_3_0': { tr: '3+0', en: '3+0' },
  'opt_room_3_1': { tr: '3+1', en: '3+1' },
  'opt_room_3_2': { tr: '3+2', en: '3+2' },
  'opt_room_4_0': { tr: '4+0', en: '4+0' },
  'opt_room_4_1': { tr: '4+1', en: '4+1' },
  'opt_room_4_2': { tr: '4+2', en: '4+2' },
  'opt_room_5_1': { tr: '5+1', en: '5+1' },
  'opt_room_5_2': { tr: '5+2', en: '5+2' },
  'opt_room_6_plus': { tr: '6+ ve üzeri', en: '6+ and above' },

  'opt_heat_stove': { tr: 'Soba', en: 'Stove' },
  'opt_heat_central': { tr: 'Merkezi', en: 'Central' },
  'opt_heat_gas_combi': { tr: 'Doğalgaz Kombi', en: 'Gas Combi' },
  'opt_heat_floor': { tr: 'Yerden Isıtma', en: 'Floor Heating' },
  'opt_heat_ac': { tr: 'Klima', en: 'AC' },
  'opt_heat_gas_stove': { tr: 'Doğalgaz Sobalı', en: 'Natural Gas Stove' },
  'opt_heat_central_share': { tr: 'Merkezi (Pay Ölçer)', en: 'Central (Shared)' },

  'opt_floor_base_1': { tr: 'Kot 1', en: 'Basement 1' },
  'opt_floor_base_2': { tr: 'Kot 2', en: 'Basement 2' },
  'opt_floor_base_3': { tr: 'Kot 3', en: 'Basement 3' },
  'opt_floor_basement': { tr: 'Bodrum', en: 'Basement' },
  'opt_floor_ground': { tr: 'Giriş Katı', en: 'Ground Floor' },
  'opt_floor_entrance': { tr: 'Giriş Katı', en: 'Entrance Floor' },
  'opt_floor_high_ent': { tr: 'Yüksek Giriş', en: 'High Entrance' },
  'opt_floor_garden': { tr: 'Bahçe Katı', en: 'Garden Floor' },
  'opt_floor_top': { tr: 'En Üst Kat', en: 'Top Floor' },
  'opt_floor_detached': { tr: 'Müstakil', en: 'Detached' },
  'opt_floor_villa': { tr: 'Villa Tipi', en: 'Villa Type' },
  'opt_floor_roof': { tr: 'Çatı Katı', en: 'Roof Floor' },

  'opt_bath_single': { tr: '1 Banyo', en: '1 Bathroom' },
  'opt_bath_master': { tr: 'Ebeveyn Banyosu', en: 'Master Bathroom' },
  'opt_bath_2_plus': { tr: '2 ve Üzeri', en: '2 or More' },

  'opt_kitchen_american': { tr: 'Amerikan Mutfak', en: 'American Kitchen' },
  'opt_kitchen_separate': { tr: 'Ayrı Mutfak', en: 'Separate Kitchen' },

  'opt_parking_open': { tr: 'Açık Otopark', en: 'Open Parking' },
  'opt_parking_closed': { tr: 'Kapalı Otopark', en: 'Closed Parking' },

  'opt_furn_yes': { tr: 'Eşyalı', en: 'Furnished' },
  'opt_furn_no': { tr: 'Eşyasız', en: 'Unfurnished' },
  'opt_furn_partial': { tr: 'Kısmen Eşyalı', en: 'Partly Furnished' },
  'opt_site_yes': { tr: 'Evet', en: 'Yes' },
  'opt_site_no': { tr: 'Hayır', en: 'No' },
  'opt_credit_yes': { tr: 'Evet', en: 'Yes' },
  'opt_credit_cash': { tr: 'Sadece Nakit', en: 'Cash Only' },

  // Deed (Tapu)
  'opt_deed_condo': { tr: 'Kat Mülkiyetli', en: 'Condominium' },
  'opt_deed_servitude': { tr: 'Kat İrtifaklı', en: 'Construction Servitude' },
  'opt_deed_land': { tr: 'Arsa Tapulu', en: 'Land Deed' },
  'opt_deed_share': { tr: 'Hisseli Tapu', en: 'Shared Deed' },

  // Usage (Kullanım)
  'opt_use_living': { tr: 'Mülk Sahibi Oturuyor', en: 'Owner Occupied' },
  'opt_use_invest': { tr: 'Kiracılı', en: 'Tenant Occupied' },
  'opt_use_empty': { tr: 'Boş', en: 'Vacant' },
  'opt_use_empty_prop': { tr: 'Boş', en: 'Vacant' },

  'opt_fuel_gas': { tr: 'Benzin', en: 'Gasoline' },
  'opt_fuel_diesel': { tr: 'Dizel', en: 'Diesel' },
  'opt_fuel_lpg': { tr: 'LPG', en: 'LPG' },
  'opt_fuel_electric': { tr: 'Elektrik', en: 'Electric' },
  'opt_fuel_hybrid': { tr: 'Hibrit', en: 'Hybrid' },

  'opt_gear_manual': { tr: 'Manuel', en: 'Manual' },
  'opt_gear_auto': { tr: 'Otomatik', en: 'Automatic' },
  'opt_gear_semi': { tr: 'Yarı Otomatik', en: 'Semi-Auto' },

  'opt_damage_none': { tr: 'Hasarsız', en: 'No Damage' },
  'opt_damage_error_free': { tr: 'Hatasız', en: 'Error Free' },
  'opt_damage_no_paint': { tr: 'Boyasız', en: 'Paint Free' },
  'opt_damage_heavy': { tr: 'Ağır Hasarlı', en: 'Heavy Damage' },
  'opt_damage_local': { tr: 'Lokal Boyalı', en: 'Local Paint' },

  'opt_col_beige': { tr: 'Bej', en: 'Beige' },
  'opt_col_white': { tr: 'Beyaz', en: 'White' },
  'opt_col_graphite': { tr: 'Füme', en: 'Graphite' },
  'opt_col_grey': { tr: 'Gri', en: 'Grey' },
  'opt_col_silver': { tr: 'Gümüş Gri', en: 'Silver' },
  'opt_col_brown': { tr: 'Kahverengi', en: 'Brown' },
  'opt_col_red': { tr: 'Kırmızı', en: 'Red' },
  'opt_col_navy': { tr: 'Lacivert', en: 'Navy Blue' },
  'opt_col_blue': { tr: 'Mavi', en: 'Blue' },
  'opt_col_met_silver': { tr: 'Metalik Gri', en: 'Metallic Silver' },
  'opt_col_met_black': { tr: 'Metalik Siyah', en: 'Metallic Black' },
  'opt_col_pearl': { tr: 'Sedef Beyaz', en: 'Pearl White' },
  'opt_col_black': { tr: 'Siyah', en: 'Black' },
  'opt_col_green': { tr: 'Yeşil', en: 'Green' },
  'opt_col_other': { tr: 'Diğer', en: 'Other' },

  'opt_exists': { tr: 'Var', en: 'Yes' },
  'opt_none': { tr: 'Yok', en: 'No' },
  'opt_any': { tr: 'Fark Etmez', en: 'Any' },

  // Static Pages & Legal
  'legal_kvkk_title': { tr: 'KVKK Aydınlatma Metni', en: 'KVKK Privacy Notice' },
  'legal_kvkk_text': {
    tr: `
6698 SAYILI KİŞİSEL VERİLERİN KORUNMASI KANUNU UYARINCA AYDINLATMA METNİ

1. VERİ SORUMLUSU
Ersan İbrahim Akyıldız - cebimegelsin.com ("Platform") olarak, 6698 sayılı Kişisel Verilerin Korunması Kanunu uyarınca kişisel verilerinizi aşağıda açıklanan kapsamda işlemekteyiz.

Adres: Çınardere Mh. Ömer Seyfettin Sk. No:6B Pendik/İstanbul
VKN: 0440480933 (Yakacık V.D.)
E-posta: info@cebimegelsin.com
Telefon: 0850 305 16 98

2. İŞLENEN KİŞİSEL VERİLER
• Kimlik Bilgileri: Ad, Soyad
• İletişim Bilgileri: Telefon numarası, E-posta adresi
• Talep Bilgileri: Oluşturduğunuz emlak veya araç talebine ilişkin detaylar (bütçe, lokasyon, özellikler vb.)
• İşlem Güvenliği Bilgileri: IP adresi, log kayıtları, cihaz bilgileri

3. KİŞİSEL VERİLERİN İŞLENME AMAÇLARI
Kişisel verileriniz;
• Platform hizmetlerinin sunulması ve talep-teklif eşleştirmesi,
• Kullanıcı hesabının oluşturulması ve yönetimi,
• Hizmet sağlayıcılarla güvenli iletişimin sağlanması,
• Yasal yükümlülüklerin yerine getirilmesi,
• Platform güvenliğinin sağlanması
amaçlarıyla işlenmektedir.

4. HUKUKİ SEBEPLER
Kişisel verileriniz, KVKK'nın 5. ve 6. maddelerinde belirtilen;
• Açık rızanızın bulunması,
• Sözleşmenin kurulması veya ifası için gerekli olması,
• Veri sorumlusunun hukuki yükümlülüğünü yerine getirmesi,
• Meşru menfaatler için zorunlu olması
hukuki sebeplerine dayanarak işlenmektedir.

5. KİŞİSEL VERİLERİN AKTARILMASI
Kişisel verileriniz, açık rızanız doğrultusunda;
• Sistemde kayıtlı ve onaylı hizmet sağlayıcılar (Emlak Ofisleri, Oto Galeriler),
• Teknik altyapı sağlayıcıları (hosting, SMS servisi),
• Yasal zorunluluk halinde yetkili kamu kurum ve kuruluşları
ile paylaşılabilecektir. Teklif sahibi firmalarla iletişim bilgileriniz SADECE sizin onayınızla paylaşılır.

6. VERİ SAKLAMA SÜRELERİ
• Üyelik bilgileri: Üyeliğin sona ermesinden itibaren 10 yıl (TTK m.82)
• Talep ve teklif kayıtları: İşlem tarihinden itibaren 3 yıl
• Log ve güvenlik kayıtları: 2 yıl (5651 sayılı Kanun)
• Ticari ileti izinleri: İzin geri çekilene kadar

Sürelerin dolmasının ardından verileriniz silinecek, yok edilecek veya anonim hale getirilecektir.

7. VERİ TOPLAMA YÖNTEMİ
Kişisel verileriniz; internet sitemiz, mobil uygulamamız üzerinden otomatik ve otomatik olmayan yöntemlerle elektronik ortamda toplanmaktadır.

8. KVKK KAPSAMINDAKİ HAKLARINIZ
KVKK'nın 11. maddesi uyarınca aşağıdaki haklara sahipsiniz:
a) Kişisel verilerinizin işlenip işlenmediğini öğrenme,
b) İşlenmişse buna ilişkin bilgi talep etme,
c) İşlenme amacını ve amaca uygun kullanılıp kullanılmadığını öğrenme,
d) Yurt içi/yurt dışında aktarıldığı üçüncü kişileri bilme,
e) Eksik/yanlış işlenmişse düzeltilmesini isteme,
f) KVKK'nın 7. maddesi kapsamında silinmesini/yok edilmesini isteme,
g) Düzeltme/silme işlemlerinin aktarıldığı üçüncü kişilere bildirilmesini isteme,
h) Münhasıran otomatik sistemlerle analiz edilmesi sonucunda aleyhinize çıkan sonuca itiraz etme,
i) Kanuna aykırı işleme sebebiyle zarara uğramanız halinde tazminat talep etme.

Haklarınızı kullanmak için info@cebimegelsin.com adresine kimliğinizi tevsik edici belgelerle birlikte yazılı başvuru yapabilirsiniz.
  `, en: `
PRIVACY NOTICE UNDER PERSONAL DATA PROTECTION LAW NO. 6698

1. DATA CONTROLLER
Ersan İbrahim Akyıldız - cebimegelsin.com ("Platform")
Address: Çınardere Mh. Ömer Seyfettin Sk. No:6B Pendik/İstanbul
Tax ID: 0440480933
E-mail: info@cebimegelsin.com

2. PROCESSED PERSONAL DATA
• Identity: Name, Surname
• Contact: Phone, E-mail
• Request Information: Property/vehicle criteria
• Security: IP address, logs

3. DATA RETENTION PERIODS
• Membership data: 10 years after termination
• Transaction records: 3 years
• Log records: 2 years
• Commercial message consents: Until withdrawn

4. YOUR RIGHTS
Under KVKK Article 11, you have the right to learn, correct, delete your data. Contact us at info@cebimegelsin.com.
  ` },
  'legal_share_title': { tr: 'Veri Paylaşım İzni ve Açık Rıza', en: 'Data Sharing Consent' },
  'legal_share_text': {
    tr: `
VERİ PAYLAŞIM İZNİ VE AÇIK RIZA BEYANI

1. VERİ PAYLAŞIMI AÇIK RIZASI
Cebime Gelsin platformunda oluşturduğum talebe ilişkin kişisel verilerimin (Ad, Soyad, Telefon, E-posta ve Talep Detayları);
• Talebime uygun teklif sunabilecek, sistemde kayıtlı ve onaylı hizmet sağlayıcılar (Emlak Ofisleri, Oto Galeriler),
• Platform teknik altyapı hizmet sağlayıcıları
ile SADECE talep-teklif sürecinin yürütülmesi amacıyla paylaşılmasına AÇIK RIZA veriyorum.

İletişim bilgilerimin, teklif sahibi firmalarla paylaşılması için ayrıca onayımın gerektiğini biliyorum.

2. TİCARİ ELEKTRONİK İLETİ ONAYI
6563 sayılı Elektronik Ticaretin Düzenlenmesi Hakkında Kanun kapsamında;
• Hizmet sağlayıcıların sundukları tekliflerle ilgili telefon, SMS veya e-posta ile iletişime geçmesine,
• Cebime Gelsin platformunun hizmet kalitesi, anket ve kampanya bilgilendirmeleri amacıyla ticari elektronik ileti göndermesine
onay veriyorum.

Bu onayımı dilediğim zaman ücretsiz olarak geri alabileceğimi ve ret hakkımın saklı olduğunu biliyorum.

3. SORUMLULUK SINIRLAMASI
Cebime Gelsin, 6563 sayılı Kanun kapsamında "Aracı Hizmet Sağlayıcı" statüsündedir ve;
• Hizmet sağlayıcılarla aramda gerçekleşecek ticari işlemlerin tarafı değildir,
• Hizmet sağlayıcıların sunduğu ürün/hizmetin kalitesi, doğruluğu ve yasallığından sorumlu değildir,
• Taraflar arasında kurulacak sözleşmelere taraf olmayacaktır.

Tüm sorumluluk ilgili hizmet sağlayıcıya aittir.

4. İZİN TARİHİ VE KAYDI
Bu onay formu elektronik ortamda, IP adresim ve zaman damgası kayıt altına alınarak saklanacaktır.
  `, en: `
DATA SHARING CONSENT AND EXPLICIT CONSENT STATEMENT

1. DATA SHARING CONSENT
I give my EXPLICIT CONSENT for my personal data (Name, Surname, Phone, E-mail, Request Details) to be shared with registered service providers (Real Estate Agencies, Auto Galleries) for the purpose of receiving offers.

2. COMMERCIAL ELECTRONIC MESSAGE CONSENT
I consent to receiving commercial electronic messages about offers, surveys, and campaigns.
I can withdraw this consent at any time free of charge.

3. LIMITATION OF LIABILITY
Cebime Gelsin is an "Intermediary Service Provider" and is not a party to transactions between users and service providers.
  ` },
  'why_title': { tr: 'Neden Cebime Gelsin?', en: 'Why Cebime Gelsin?' },
  'why_desc': { tr: 'Alışılagelmiş arama yöntemlerini unutun. Modern, hızlı ve güvenli ticaret deneyimi.', en: 'Forget traditional search methods. Modern, fast and secure trade experience.' },
  'prop_trust_title': { tr: 'Güvenilir Firmalar', en: 'Trusted Firms' },
  'prop_trust_desc': { tr: 'Tüm üyelerimiz kurumsal kimlik doğrulaması yapılmış seçkin firmalardır.', en: 'All members are elite firms with corporate identity verification.' },
  'prop_time_title': { tr: 'Zaman Tasarrufu', en: 'Save Time' },
  'prop_time_desc': { tr: 'İlanlar arasında kaybolmak yerine, bırakın teklifler size gelsin.', en: 'Instead of getting lost in listings, let offers come to you.' },
  'prop_price_title': { tr: 'En İyi Fiyat', en: 'Best Price' },
  'prop_price_desc': { tr: 'Firmalar arası rekabet sayesinde piyasadaki en iyi fiyatları yakalayın.', en: 'Catch the best prices thanks to competition between firms.' },
  'prop_comm_title': { tr: 'Kolay İletişim', en: 'Easy Contact' },
  'prop_comm_desc': { tr: 'Dilediğiniz teklifi seçin, firma ile anında mesajlaşmaya başlayın.', en: 'Choose the offer you want, start messaging with the firm immediately.' },
  'test_title': { tr: 'Kullanıcılarımız Ne Diyor?', en: 'What Users Say?' },
  'test_desc': { tr: 'Binlerce mutlu kullanıcı deneyimini paylaşıyor.', en: 'Thousands of happy users sharing their experience.' },
  'review_1_role': { tr: 'Ev Sahibi', en: 'Home Owner' },
  'review_1_text': { tr: 'Ev arama sürecim hiç bu kadar kolay olmamıştı. Teşekkürler!', en: 'My home search process has never been easier. Thanks!' },
  'review_2_role': { tr: 'Araç Alıcısı', en: 'Car Buyer' },
  'review_2_text': { tr: 'Aradığım aracı 24 saat içinde buldum. Harika sistem.', en: 'Found the car I was looking for in 24 hours. Great system.' },
  'review_3_role': { tr: 'Kiracı', en: 'Tenant' },
  'review_3_text': { tr: 'Emlakçılarla tek tek görüşmek yerine teklif almak çok pratik.', en: 'Getting offers instead of meeting realtors one by one is very practical.' },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('tr');

  const t = (key: string): string => {
    if (!translations[key]) return key;
    return translations[key][language] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within a LanguageProvider');
  return context;
};